import React, { useState, useEffect } from 'react';
import { baseUrl } from '../../shared';
import dayjs from 'dayjs';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import CircularProgress from '@mui/material/CircularProgress';

import { EquipmentSelector } from './CalibrationShared';

const authHeaders = () => ({
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

const PCT_POINTS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

const makeRows = () => PCT_POINTS.map(pct => ({ pct, p1: '', p2: '', p3: '' }));

const EMPTY_FORM = {
  type: 'GAUGE',
  technician: '', calibrationDate: '', calDueDate: '',
  temperature: '', humidity: '', comments: '',
  maxRange: '', unit: 'PSI', maxErrorPct: 5,
};

function calcGaugeRow(row, maxRange, maxErrorPct) {
  const vals = [row.p1, row.p2, row.p3].map(v => parseFloat(v)).filter(v => !isNaN(v));
  const mr = parseFloat(maxRange);
  const errPct = parseFloat(maxErrorPct);
  if (vals.length === 0 || isNaN(mr) || mr === 0 || isNaN(errPct)) return null;
  const target = (mr * row.pct) / 100;
  const tolerance = (mr * errPct) / 100;
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
  const deviation = avg - target;
  const pass = Math.abs(deviation) <= tolerance;
  return { target, tolerance, avg, deviation, pass };
}

function GaugeMeasurementTable({ title, rows, onChange, maxRange, maxErrorPct, unit, readOnly }) {
  const setCell = (i, f) => e => onChange(rows.map((row, idx) => idx === i ? { ...row, [f]: e.target.value } : row));

  const calcs = rows.map(r => calcGaugeRow(r, maxRange, maxErrorPct));

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, textTransform: 'uppercase', fontSize: 11, color: 'text.secondary' }}>
        {title}
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 620 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'var(--surface-2)' }}>
              {['%', `Target (${unit})`, `Pass 1 (${unit})`, `Pass 2 (${unit})`, `Pass 3 (${unit})`, `Avg (${unit})`, `Tol ±(${unit})`, 'Dev', 'P/F'].map(h => (
                <TableCell key={h} sx={{ fontWeight: 600, fontSize: 11 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => {
              const c = calcs[i];
              return (
                <TableRow key={i} sx={{ bgcolor: c ? (c.pass ? 'var(--pass-bg)' : 'var(--fail-bg)') : undefined }}>
                  <TableCell sx={{ fontWeight: 600 }}>{row.pct}%</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{c ? c.target.toFixed(2) : '—'}</TableCell>
                  {['p1', 'p2', 'p3'].map(p => (
                    <TableCell key={p}>
                      {readOnly ? (row[p] || '—') : (
                        <TextField size="small" variant="standard" type="number" value={row[p]} onChange={setCell(i, p)} sx={{ width: 70 }} inputProps={{ style: { fontSize: 13 } }} />
                      )}
                    </TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: 600 }}>{c ? c.avg.toFixed(2) : '—'}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{c ? `±${c.tolerance.toFixed(2)}` : '—'}</TableCell>
                  <TableCell sx={{ color: c && !c.pass ? 'error.main' : undefined, fontSize: 12 }}>
                    {c ? `${c.deviation >= 0 ? '+' : ''}${c.deviation.toFixed(2)}` : '—'}
                  </TableCell>
                  <TableCell>
                    {c && <Chip label={c.pass ? 'PASS' : 'FAIL'} size="small" sx={{ bgcolor: c.pass ? 'var(--pass-bg)' : 'var(--fail-bg)', color: c.pass ? 'var(--pass)' : 'var(--fail)', fontWeight: 700, fontSize: 11 }} />}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

export default function GaugeCalForm({ open, onClose, job, cal, canManage, onSaved }) {
  const readOnly = !canManage;
  const [form, setForm] = useState(EMPTY_FORM);
  const [asFoundRising, setAsFoundRising] = useState(makeRows());
  const [asFoundFalling, setAsFoundFalling] = useState(makeRows());
  const [asLeftRising, setAsLeftRising] = useState(makeRows());
  const [asLeftFalling, setAsLeftFalling] = useState(makeRows());
  const [equipmentUsed, setEquipmentUsed] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (cal) {
      setForm({
        type: cal.type || 'GAUGE',
        technician: cal.technician || '',
        calibrationDate: cal.calibrationDate ? dayjs(cal.calibrationDate).format('YYYY-MM-DD') : '',
        calDueDate: cal.calDueDate ? dayjs(cal.calDueDate).format('YYYY-MM-DD') : '',
        temperature: cal.temperature != null ? String(cal.temperature) : '',
        humidity: cal.humidity != null ? String(cal.humidity) : '',
        comments: cal.comments || '',
        maxRange: cal.rawData?.maxRange || '',
        unit: cal.rawData?.unit || 'PSI',
        maxErrorPct: cal.rawData?.maxErrorPct ?? 5,
      });
      const rd = cal.rawData || {};
      setAsFoundRising(rd.asFoundRising?.length ? rd.asFoundRising : makeRows());
      setAsFoundFalling(rd.asFoundFalling?.length ? rd.asFoundFalling : makeRows());
      setAsLeftRising(rd.asLeftRising?.length ? rd.asLeftRising : makeRows());
      setAsLeftFalling(rd.asLeftFalling?.length ? rd.asLeftFalling : makeRows());
      setEquipmentUsed(rd.equipmentSnapshot || []);
    } else {
      setForm({ ...EMPTY_FORM });
      setAsFoundRising(makeRows());
      setAsFoundFalling(makeRows());
      setAsLeftRising(makeRows());
      setAsLeftFalling(makeRows());
      setEquipmentUsed([]);
    }
  }, [open, cal]);

  useEffect(() => {
    if (form.calibrationDate && !cal?.calDueDate) {
      setForm(f => ({ ...f, calDueDate: dayjs(form.calibrationDate).add(12, 'month').format('YYYY-MM-DD') }));
    }
  }, [form.calibrationDate]);

  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  const allTables = [...asFoundRising, ...asFoundFalling, ...asLeftRising, ...asLeftFalling];
  const allCalcs = allTables.map(r => calcGaugeRow(r, form.maxRange, form.maxErrorPct));
  const allComplete = allCalcs.every(c => c !== null);
  const overallResult = allComplete ? (allCalcs.every(c => c.pass) ? 'PASS' : 'FAIL') : null;

  const handleSave = async () => {
    if (equipmentUsed.length === 0) {
      alert('Please select the test equipment used before saving.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        technician: form.technician,
        calibrationDate: form.calibrationDate,
        calDueDate: form.calDueDate,
        temperature: form.temperature,
        humidity: form.humidity,
        comments: form.comments,
        type: form.type,
        overallResult: overallResult || null,
        rawData: {
          maxRange: form.maxRange, unit: form.unit, maxErrorPct: form.maxErrorPct,
          asFoundRising, asFoundFalling, asLeftRising, asLeftFalling,
          equipmentSnapshot: equipmentUsed,
        },
      };
      const url = cal
        ? baseUrl + 'workshop/jobs/' + job.id + '/calibrations/' + cal.id
        : baseUrl + 'workshop/jobs/' + job.id + '/calibrations';
      const res = await fetch(url, {
        method: cal ? 'PUT' : 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) onSaved();
    } finally { setSaving(false); }
  };

  const typeLabel = form.type === 'DIAL' ? 'Dial Indicator Calibration' : 'Gauge Calibration';
  const tableProps = { maxRange: form.maxRange, maxErrorPct: form.maxErrorPct, unit: form.unit, readOnly };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
            {cal ? `${typeLabel} — ${cal.certNo || 'Draft'}` : `New ${typeLabel}`}
          </Typography>
          {overallResult && (
            <Chip label={overallResult} sx={{ bgcolor: overallResult === 'PASS' ? 'var(--pass-bg)' : 'var(--fail-bg)', color: overallResult === 'PASS' ? 'var(--pass)' : 'var(--fail)', fontWeight: 700, fontSize: 14 }} />
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}><Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>Calibration Details</Typography><Divider sx={{ mt: 0.5, mb: 1.5 }} /></Grid>

          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small" disabled={readOnly || !!cal}>
              <InputLabel>Type</InputLabel>
              <Select value={form.type} label="Type" onChange={set('type')}>
                <MenuItem value="GAUGE">Gauge Calibration</MenuItem>
                <MenuItem value="DIAL">Dial Indicator</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}><TextField size="small" fullWidth label="Technician" value={form.technician} onChange={set('technician')} InputProps={{ readOnly }} /></Grid>
          <Grid item xs={12} sm={3}><TextField size="small" fullWidth label="Calibration Date" type="date" value={form.calibrationDate} onChange={set('calibrationDate')} InputLabelProps={{ shrink: true }} InputProps={{ readOnly }} /></Grid>
          <Grid item xs={12} sm={2}><TextField size="small" fullWidth label="Cal Due Date" type="date" value={form.calDueDate} onChange={set('calDueDate')} InputLabelProps={{ shrink: true }} InputProps={{ readOnly }} /></Grid>
          <Grid item xs={12} sm={2}><TextField size="small" fullWidth label="Temperature (°C)" type="number" value={form.temperature} onChange={set('temperature')} InputProps={{ readOnly }} /></Grid>
          <Grid item xs={12} sm={2}><TextField size="small" fullWidth label="Humidity (%)" type="number" value={form.humidity} onChange={set('humidity')} InputProps={{ readOnly }} /></Grid>
          <Grid item xs={12} sm={2}><TextField size="small" fullWidth label="Max Range" type="number" value={form.maxRange} onChange={set('maxRange')} InputProps={{ readOnly }} helperText="Defines target values" /></Grid>
          <Grid item xs={12} sm={2}><TextField size="small" fullWidth label="Unit (e.g. PSI, bar)" value={form.unit} onChange={set('unit')} InputProps={{ readOnly }} /></Grid>
          <Grid item xs={12} sm={2}><TextField size="small" fullWidth label="Max Error % (full scale)" type="number" value={form.maxErrorPct} onChange={set('maxErrorPct')} InputProps={{ readOnly }} /></Grid>
          <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Comments" value={form.comments} onChange={set('comments')} InputProps={{ readOnly }} /></Grid>

          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>Test Equipment Used</Typography>
            <Divider sx={{ mt: 0.5, mb: 1.5 }} />
            <EquipmentSelector selected={equipmentUsed} onChange={setEquipmentUsed} readOnly={readOnly} />
          </Grid>

          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>Measurement Data</Typography>
            <Divider sx={{ mt: 0.5, mb: 1.5 }} />
          </Grid>

          <Grid item xs={12}>
            <GaugeMeasurementTable title="AS FOUND — Rising (Before Service)" rows={asFoundRising} onChange={setAsFoundRising} {...tableProps} />
          </Grid>
          <Grid item xs={12} sx={{ mt: 2 }}>
            <GaugeMeasurementTable title="AS FOUND — Falling (Before Service)" rows={asFoundFalling} onChange={setAsFoundFalling} {...tableProps} />
          </Grid>
          <Grid item xs={12} sx={{ mt: 2 }}>
            <GaugeMeasurementTable title="AS LEFT — Rising (After Service)" rows={asLeftRising} onChange={setAsLeftRising} {...tableProps} />
          </Grid>
          <Grid item xs={12} sx={{ mt: 2 }}>
            <GaugeMeasurementTable title="AS LEFT — Falling (After Service)" rows={asLeftFalling} onChange={setAsLeftFalling} {...tableProps} />
          </Grid>

          {overallResult && (
            <Grid item xs={12}>
              <Box sx={{ mt: 1, p: 2, borderRadius: 1, textAlign: 'center', bgcolor: overallResult === 'PASS' ? 'var(--pass-bg)' : 'var(--fail-bg)' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: overallResult === 'PASS' ? 'var(--pass)' : 'var(--fail)' }}>OVERALL RESULT: {overallResult}</Typography>
                <Typography variant="caption" color="text.secondary">Max error ±{form.maxErrorPct}% of full scale ({form.maxRange} {form.unit})</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Close</Button>
        {canManage && (
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : cal ? 'Save Changes' : 'Save Calibration'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
